// src/components/pages/mypage/hooks/usePortfolios.js

import { useState, useEffect, useMemo, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_BASE_URL;

export default function usePortfolios(auth, handleConfirm, t) {
  const [portfolios, setPortfolios] = useState([]);
  const [loadingPortfolios, setLoadingPortfolios] = useState(true);
  const [showNewPortfolioModal, setShowNewPortfolioModal] = useState(false);
  const [showRebalanceModal, setShowRebalanceModal] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);

  const portfoliosUrl = `${API_URL}/portfolios/my`;

  const fetchPortfolios = useCallback(async () => {
    if (!auth.isAuthenticated) return;
    setLoadingPortfolios(true);
    try {
      const res = await fetch(portfoliosUrl, {
        headers: { Authorization: `Bearer ${auth.user?.id_token}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch portfolios (${res.status})`);
      const data = await res.json();
      setPortfolios(data);
    } catch (err) {
      console.error('Failed to load portfolios', err);
    } finally {
      setLoadingPortfolios(false);
    }
  }, [auth.isAuthenticated, auth.user?.id_token, portfoliosUrl]);

  // 포트폴리오 로드
  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  // 평균 수익률/샤프 비율 계산
  const { averageReturn, averageSharpe } = useMemo(() => {
    if (!portfolios.length) {
      return { averageReturn: 0, averageSharpe: 0 };
    }
    const totalReturn = portfolios.reduce((sum, p) => sum + (p.ret_1y || 0), 0);
    const totalSharpe = portfolios.reduce((sum, p) => sum + (p.sharpe_1y || 0), 0);
    return {
      averageReturn: totalReturn / portfolios.length,
      averageSharpe: totalSharpe / portfolios.length,
    };
  }, [portfolios]);

  const handleOpenRebalanceModal = useCallback((portfolio) => {
    setSelectedPortfolio(portfolio);
    setShowRebalanceModal(true);
  }, []);

  const handleCloseRebalanceModal = useCallback(() => {
    setShowRebalanceModal(false);
    setSelectedPortfolio(null);
  }, []);
  
  // 리밸런싱 완료 후 목록 새로고침
  const handleRebalanced = fetchPortfolios; 

  // 포트폴리오 삭제
  const handleDeletePortfolio = useCallback(async (portfolioId) => {
    handleConfirm({
      message: t('mypage.confirm.deletePortfolio'),
      callback: async () => {
        try {
          const res = await fetch(`${API_URL}/portfolios/${portfolioId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.user.id_token}` },
          });
          if (!res.ok) {
            const errorData = await res.json();
            throw new Error(errorData.error || `Failed to delete portfolio (${res.status})`);
          }
          setPortfolios(prev => prev.filter(p => p.portfolioId !== portfolioId));
          alert(t('mypage.alert.portfolioDeleted'));
        } catch (err) {
          console.error('Failed to delete portfolio', err);
          alert(`${t('mypage.error.deletePortfolio')} ${err.message}`);
        }
      }
    });
  }, [auth.user?.id_token, handleConfirm, t]);


  return {
    portfolios,
    loadingPortfolios,
    averageReturn,
    averageSharpe,
    showNewPortfolioModal,
    setShowNewPortfolioModal,
    showRebalanceModal,
    selectedPortfolio,
    handleOpenRebalanceModal,
    handleCloseRebalanceModal,
    handleRebalanced,
    handleDeletePortfolio,
  };
}